import { FluxService } from '../../service/FluxService';
import {
  type FluxProductionDataShape,
  SUMMARY_FLOW_VAR_INPUT_ID,
  extractTextFromFluxProductionMessage,
  getFluxProductionErrorMessage,
} from './fluxProductionMessageText';
import type { VarInputNodeSnapshot } from './variableInputsExample';

const TEXT_TRUNCATE = 500;

function truncateInputText(text: string, max: number): string {
  if (text.length <= max) {
    return text;
  }
  return `${text.slice(0, max)}...`;
}

export type DataInputPurposeRow = {
  index: number;
  inputId: string;
  purpose: string;
};

async function fetchPurposeForNode(
  node: VarInputNodeSnapshot,
  index: number,
  summaryFlowId: string,
  agentSummary: string,
): Promise<DataInputPurposeRow> {
  const label = node.data.label ? String(node.data.label) : 'Data input';

  const inputText = JSON.stringify({
    task: 'dataInputPurpose',
    inputId: node.id,
    label,
    text: truncateInputText(node.data.text ?? '', TEXT_TRUNCATE),
    agentSummary,
  });

  const variableInputs = [
    {
      inputId: SUMMARY_FLOW_VAR_INPUT_ID,
      inputText,
      links: [],
      files: [],
    },
  ];

  try {
    const response = await FluxService.FluxApiProduction(summaryFlowId, {
      variableInputs,
    });

    const payload = response?.data as FluxProductionDataShape | undefined;
    const err = getFluxProductionErrorMessage(payload);
    if (err) {
      return { index, inputId: node.id, purpose: label };
    }

    const purposeLine = extractTextFromFluxProductionMessage(
      payload,
      label,
    ).trim();
    const purpose =
      purposeLine && purposeLine !== 'No summary available'
        ? `${label}: ${purposeLine}`
        : label;

    return { index, inputId: node.id, purpose };
  } catch {
    return { index, inputId: node.id, purpose: label };
  }
}

export async function buildApiSchemaTableRowsWithPurposes(params: {
  snapshots: VarInputNodeSnapshot[];
  summaryFlowId: string;
  agentSummary: string;
}): Promise<DataInputPurposeRow[]> {
  const { snapshots, summaryFlowId, agentSummary } = params;

  if (!summaryFlowId) {
    return snapshots.map((node, i) => ({
      index: i + 1,
      inputId: node.id,
      purpose: node.data.label ? String(node.data.label) : 'Data input',
    }));
  }

  return Promise.all(
    snapshots.map((node, i) =>
      fetchPurposeForNode(node, i + 1, summaryFlowId, agentSummary),
    ),
  );
}
